<template>
  <transition name="slide-down">
    <div v-if="visible" ref="bannerEl" class="network-banner" :class="bannerClass" role="alert">
      <span class="material-symbols-outlined banner-icon">{{ icon }}</span>
      <span class="banner-message">{{ message }}</span>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g']
const SLOW_DOWNLINK_MBPS = 0.5
const RESTORED_DISPLAY_MS = 3000
const PROBE_INTERVAL_MS = 15000
const PROBE_TIMEOUT_MS = 5000
const SLOW_PROBE_MS = 2000
const UNSTABLE_WINDOW_MS = 60000
const UNSTABLE_FLIP_THRESHOLD = 3

// navigator.onLine only reflects whether the network interface is
// associated (e.g. wifi connected to a router) — it stays true even if
// the router has no internet upstream. hasInternet is a real reachability
// check against the API host to catch that case.
const probeOrigin = (() => {
  try {
    return new URL(process.env.VUE_APP_API_ROOT_URL).origin
  } catch {
    return window.location.origin
  }
})()

const isOnline = ref(navigator.onLine)
const hasInternet = ref(true)
// connectionApiSlow comes from the Network Information API (Chromium-only).
// probeSlow times the same reachability probe used for hasInternet, so
// Safari/Firefox — which don't support navigator.connection — still get
// slow-network detection.
const connectionApiSlow = ref(false)
const probeSlow = ref(false)
let probeTimer = null
let probeInFlight = false

const probeConnectivity = async () => {
  if (probeInFlight) return
  probeInFlight = true
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  const startedAt = performance.now()
  try {
    await fetch(`${probeOrigin}/?_=${Date.now()}`, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal
    })
    hasInternet.value = true
    probeSlow.value = performance.now() - startedAt > SLOW_PROBE_MS
  } catch {
    hasInternet.value = false
    probeSlow.value = false
  } finally {
    clearTimeout(timeoutId)
    probeInFlight = false
  }
}

const checkConnectionSpeed = () => {
  if (!connection) return
  connectionApiSlow.value = SLOW_EFFECTIVE_TYPES.includes(connection.effectiveType) ||
    (typeof connection.downlink === 'number' && connection.downlink < SLOW_DOWNLINK_MBPS)
}

const isSlow = computed(() => connectionApiSlow.value || probeSlow.value)

// A connection that keeps dropping and coming back within a short window
// isn't well described by "offline" or "slow" — it flashes between the two
// (or between offline and fine). Track connected/disconnected flips in a
// rolling window and surface that pattern as its own "unstable" status
// instead of letting the banner flicker through offline/restored repeatedly.
const isConnected = computed(() => isOnline.value && hasInternet.value)
const flipTimestamps = ref([])
const isUnstable = ref(false)
let unstableResetTimer = null

const recordFlip = () => {
  const now = Date.now()
  flipTimestamps.value = [...flipTimestamps.value, now].filter((t) => now - t < UNSTABLE_WINDOW_MS)
  isUnstable.value = flipTimestamps.value.length >= UNSTABLE_FLIP_THRESHOLD

  clearTimeout(unstableResetTimer)
  unstableResetTimer = setTimeout(() => {
    const cutoff = Date.now()
    flipTimestamps.value = flipTimestamps.value.filter((t) => cutoff - t < UNSTABLE_WINDOW_MS)
    isUnstable.value = flipTimestamps.value.length >= UNSTABLE_FLIP_THRESHOLD
  }, UNSTABLE_WINDOW_MS)
}

watch(isConnected, recordFlip)

const status = computed(() => {
  if (!isOnline.value || !hasInternet.value) return 'offline'
  if (isUnstable.value) return 'unstable'
  if (isSlow.value) return 'slow'
  return null
})

// Debounced so a momentary blip (e.g. connection events firing during
// normal page load) doesn't flash the banner on and off. A prior
// offline/slow spell is remembered (hadIssue) so recovery briefly shows a
// "back online" confirmation instead of just silently vanishing — but only
// when there was actually something to recover from, not on first load.
const visible = ref(false)
const activeStatus = ref(null)
const hadIssue = ref(false)
let showTimer = null
let restoredTimer = null

watch(status, (newStatus) => {
  clearTimeout(showTimer)
  clearTimeout(restoredTimer)

  if (newStatus) {
    hadIssue.value = true
    showTimer = setTimeout(() => {
      activeStatus.value = newStatus
      visible.value = true
    }, 800)
    return
  }

  if (hadIssue.value) {
    hadIssue.value = false
    activeStatus.value = 'restored'
    visible.value = true
    restoredTimer = setTimeout(() => {
      visible.value = false
      activeStatus.value = null
    }, RESTORED_DISPLAY_MS)
  } else {
    visible.value = false
    activeStatus.value = null
  }
}, { immediate: true })

const bannerClass = computed(() => `banner-${activeStatus.value}`)
const icon = computed(() => {
  if (activeStatus.value === 'offline') return 'wifi_off'
  if (activeStatus.value === 'restored') return 'wifi'
  if (activeStatus.value === 'unstable') return 'sync_problem'
  return 'network_check'
})
const message = computed(() => {
  if (activeStatus.value === 'offline') return "You're offline. Check your internet connection."
  if (activeStatus.value === 'restored') return "You're back online."
  if (activeStatus.value === 'unstable') return 'Your connection seems unstable — some actions may be interrupted.'
  return 'Slow network connection detected — some actions may take longer than usual.'
})

const handleOnline = () => {
  isOnline.value = true
  probeConnectivity()
}
const handleOffline = () => { isOnline.value = false }

// Rather than overlay the app header/sidebar, the banner's own height is
// published as a CSS var so dashboard.css can push the fixed-position
// header/sidebar/content down to make room for it (see .dashboard-layout
// rules) instead of the banner covering them.
const bannerEl = ref(null)
let resizeObserver = null

const updateOffset = () => {
  const height = visible.value && bannerEl.value ? bannerEl.value.offsetHeight : 0
  document.documentElement.style.setProperty('--network-banner-height', `${height}px`)
}

watch([visible, activeStatus], async () => {
  await nextTick()
  updateOffset()
  if (resizeObserver) {
    resizeObserver.disconnect()
    if (visible.value && bannerEl.value) {
      resizeObserver.observe(bannerEl.value)
    }
  }
})

onMounted(() => {
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  if (connection) {
    checkConnectionSpeed()
    connection.addEventListener('change', checkConnectionSpeed)
  }
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(updateOffset)
  }
  probeConnectivity()
  probeTimer = setInterval(probeConnectivity, PROBE_INTERVAL_MS)
})

onBeforeUnmount(() => {
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  clearInterval(probeTimer)
  if (connection) {
    connection.removeEventListener('change', checkConnectionSpeed)
  }
  resizeObserver?.disconnect()
  clearTimeout(showTimer)
  clearTimeout(restoredTimer)
  clearTimeout(unstableResetTimer)
  document.documentElement.style.removeProperty('--network-banner-height')
})
</script>

<style scoped>
.network-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #fff;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.banner-offline {
  background-color: #dc3545;
}

.banner-slow {
  background-color: #fd7e14;
}

.banner-restored {
  background-color: #198754;
}

.banner-unstable {
  background-color: #fd7e14;
}

.banner-icon {
  font-size: 1.1rem;
  line-height: 1;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
